import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

/* "Skip to main content": the first tab stop on every page (App.js renders it
   before any navbar). Hidden until focused; styled as .cn-skip in
   styles/premium.css.

   Pages do not all have id="main", so it falls back to the first <main>, then
   the first <h1>. The target gets tabIndex -1 so it can take focus without
   becoming a tab stop. After each route change the first <main> also gets
   id="main" when no element has it, so the href points at a real target. */
function findMainTarget() {
  return (
    document.getElementById("main") ||
    document.querySelector("main") ||
    document.querySelector("h1")
  );
}

const SkipLink = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  // Pages can render their <main> late (after sign-in state or data loads),
  // so watch the page until one appears, then stop. Gives up after 10s.
  useEffect(() => {
    const labelMain = () => {
      if (document.getElementById("main")) return true;
      const main = document.querySelector("main");
      if (!main) return false;
      if (!main.id) main.id = "main";
      return true;
    };
    if (labelMain()) return undefined;
    const observer = new MutationObserver(() => {
      if (labelMain()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    const giveUp = setTimeout(() => observer.disconnect(), 10000);
    return () => {
      observer.disconnect();
      clearTimeout(giveUp);
    };
  }, [pathname]);

  const skip = (event) => {
    event.preventDefault();
    const target = findMainTarget();
    if (!target) return;
    if (!target.hasAttribute("tabindex")) target.tabIndex = -1;
    target.focus();
    if (target.scrollIntoView) target.scrollIntoView({ block: "start" });
  };

  return (
    <a className="cn-skip" href="#main" onClick={skip}>
      {t("a11y.skipToContent")}
    </a>
  );
};

export default SkipLink;
